import React, {useContext} from 'react'
import {ObjUtil} from 'yougile-util'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {useUpdateForwardedRef} from '@containers/hooks'
import {
  TwContainer,
  TwIconLeft,
  TwIconRight,
  TwInput,
  TwLabel,
  TwSupport,
  TwWrap
} from './containers'
import {InputProps} from './types'
import {useInput} from './useInput'

const PropsContext = React.createContext(undefined as unknown as InputProps)

export const Input = React.forwardRef((props: InputProps, ref) => (
  <PropsContext.Provider value={props}>
    <InputBase ref={ref} />
  </PropsContext.Provider>
))

export const InputBase = React.forwardRef((_, ref) => {
  const props = useContext(PropsContext)
  const {label, support} = props

  if (label && support) {
    return (
      <TwWrap>
        <Label />
        <InputComponent ref={ref} />
        <Support />
      </TwWrap>
    )
  }
  if (label) {
    return (
      <TwWrap>
        <Label />
        <InputComponent ref={ref} />
      </TwWrap>
    )
  }
  if (support) {
    return (
      <TwWrap>
        <InputComponent ref={ref} />
        <Support />
      </TwWrap>
    )
  }
  return <InputComponent ref={ref} />
})

const Label = () => {
  const props = useContext(PropsContext)
  const {label, mark = false, disabled = false} = props
  return (
    <TwLabel $disabled={disabled}>
      {label}
      {mark && <span className="text-error">&nbsp;*</span>}
    </TwLabel>
  )
}

const Support = () => {
  const props = useContext(PropsContext)
  const {support, disabled = false} = props
  return <TwSupport $disabled={disabled}>{support}</TwSupport>
}

const svgSize = 16
const iconTablerViewBox = '0 0 24 24'

const InputComponent = React.forwardRef((_, forwardedRef) => {
  const {
    disabled = false,
    icon,
    iconPosition = 'left',
    className,
    inputClassName,
    hasError = false,
    isCorrect,
    size = '40',
    borders = true,
    height,
    style,
    rounded = '4',
    ...props
  } = useContext(PropsContext)
  const leftPosition = iconPosition === 'left' || iconPosition === 'both'
  const rightPosition = iconPosition === 'right' || iconPosition === 'both'
  const leftIconPosition = Boolean(icon && leftPosition)
  const rightIconPosition = Boolean(icon && rightPosition)

  const {validationType, ref, ...actions} = useInput(props)
  const updateRef = useUpdateForwardedRef(ref, forwardedRef)

  return (
    <TwContainer className={className} $size={size} style={style}>
      {leftIconPosition && icon && (
        <TwIconLeft>
          <IconTabler
            customSize={svgSize}
            icon={icon}
            viewBox={iconTablerViewBox}
          />
        </TwIconLeft>
      )}
      {rightIconPosition && icon && (
        <TwIconRight>
          <IconTabler
            customSize={svgSize}
            icon={icon}
            viewBox={iconTablerViewBox}
          />
        </TwIconRight>
      )}
      <TwInput
        {...ObjUtil.pick(
          props,
          'autoComplete',
          'autoFocus',
          'name',
          'placeholder',
          'value',
          'defaultValue',
          'type',
          'min',
          'onKeyUp'
        )}
        {...actions}
        ref={updateRef}
        $disabled={disabled}
        $hasError={props.isValid ? validationType === 'error' : hasError}
        $isSuccess={props.isValid ? validationType === 'success' : isCorrect}
        $leftIcon={leftIconPosition}
        $rightIcon={rightIconPosition}
        className={inputClassName}
        $size={size}
        $height={height}
        $borders={borders}
        $rounded={rounded}
      />
    </TwContainer>
  )
})
